/**
 * 全局键盘控制器 - 单例模式
 * 用于管理系统键盘的打开和关闭，确保只有一个监听器
 */
class KeyboardController {
  private static instance: KeyboardController | null = null;

  static getInstance(): KeyboardController {
    if (!KeyboardController.instance) {
      KeyboardController.instance = new KeyboardController();
    }
    return KeyboardController.instance;
  }

  private inputElement: HTMLInputElement | null = null;

  private hiddenButton: HTMLButtonElement | null = null;

  private closeCallback: (() => void) | null = null;

  // 上次关闭时间戳，用于防抖
  private lastCloseTime = 0;

  // 上次打开时间戳
  private lastOpenTime = 0;

  // 关闭操作是否正在执行
  private isClosing = false;

  // 关闭定时器
  private closeTimer: number | null = null;

  private constructor() {
    // 创建隐藏按钮（用于转移焦点）
    this.hiddenButton = document.createElement('button');
    this.hiddenButton.style.cssText =
      'position:fixed;top:-9999px;left:-9999px;opacity:0;pointer-events:none;width:1px;height:1px;';
    this.hiddenButton.setAttribute('aria-hidden', 'true');
    this.hiddenButton.setAttribute('tabindex', '-1');
    document.body.appendChild(this.hiddenButton);
  }

  // 注册输入框
  register(input: HTMLInputElement, onClose: () => void) {
    this.inputElement = input;
    this.closeCallback = onClose;
  }

  // 注销输入框
  unregister() {
    this.inputElement = null;
    this.closeCallback = null;
    this.clearCloseTimer();
  }

  // 检查是否在防抖期间（500ms 内不允许再次拉起键盘）
  isInDebounce(): boolean {
    return Date.now() - this.lastCloseTime < 500;
  }

  // 关闭键盘 - 强制执行多重保险
  closeKeyboard(): boolean {
    if (this.isClosing) {
      // 已在关闭中，直接复用当前流程，清理旧定时器后继续执行
      console.log('[KeyboardController] 关闭中重复触发，复用并重置定时器');
      this.clearCloseTimer();
    } else {
      console.log('[KeyboardController] 开始关闭键盘');
      this.isClosing = true;
    }

    // 每次调用都重置关闭时间与定时器
    this.clearCloseTimer();
    this.lastCloseTime = Date.now();

    // 立即触发回调
    if (this.closeCallback) {
      this.closeCallback();
    }

    // 多重保险关闭方案
    const forceClose = () => {
      // 方案 1: blur 输入框（重复多次）
      if (this.inputElement) {
        this.inputElement.blur();
        setTimeout(() => this.inputElement?.blur(), 10);
        setTimeout(() => this.inputElement?.blur(), 50);
      }

      // 方案 2: 焦点转移到隐藏按钮
      if (this.hiddenButton) {
        this.hiddenButton.focus();
        setTimeout(() => {
          if (this.hiddenButton) {
            this.hiddenButton.blur();
          }
        }, 50);
      }

      // 方案 3: blur 当前活动元素
      setTimeout(() => {
        if (document.activeElement && document.activeElement instanceof HTMLElement) {
          if (document.activeElement !== this.hiddenButton) {
            document.activeElement.blur();
          }
        }
      }, 100);

      // 方案 4: 使用 input 的 readonly 属性（某些系统有效）
      if (this.inputElement) {
        this.inputElement.setAttribute('readonly', 'readonly');
        setTimeout(() => {
          this.inputElement?.removeAttribute('readonly');
        }, 300);
      }
    };

    // 立即执行
    forceClose();

    // 延迟再执行一次（双保险）
    setTimeout(forceClose, 100);

    // 400ms 后重置状态（稍微加快恢复，允许更频繁关闭）
    this.closeTimer = window.setTimeout(() => {
      this.isClosing = false;
      console.log('[KeyboardController] 关闭操作完成');
    }, 400);

    return true;
  }

  // 打开键盘
  openKeyboard() {
    if (!this.inputElement) {
      console.log('[KeyboardController] 无输入框，跳过打开');
      return;
    }

    // 如果在防抖期间，不拉起键盘
    if (this.isInDebounce()) {
      console.log('[KeyboardController] 防抖期间，跳过打开');
      return;
    }

    // 如果键盘已经打开，不重复打开
    if (this.isKeyboardOpen()) {
      console.log('[KeyboardController] 键盘已打开，跳过');
      return;
    }

    console.log('[KeyboardController] 打开键盘');
    this.lastOpenTime = Date.now();
    this.inputElement.focus();
  }

  // 强制关闭系统键盘（路由变化时调用）
  forceCloseSystemKeyboard() {
    console.log('[KeyboardController] 路由变化，强制关闭键盘');

    // 重置所有状态
    this.isClosing = false;
    this.clearCloseTimer();

    // 记录关闭时间
    this.lastCloseTime = Date.now();

    // 触发回调
    if (this.closeCallback) {
      this.closeCallback();
    }

    // 多重关闭方案
    const performClose = () => {
      // blur 输入框
      if (this.inputElement) {
        this.inputElement.blur();
        this.inputElement.setAttribute('readonly', 'readonly');
        setTimeout(() => this.inputElement?.removeAttribute('readonly'), 300);
      }

      // 使用隐藏按钮
      if (this.hiddenButton) {
        this.hiddenButton.focus();
        setTimeout(() => this.hiddenButton?.blur(), 50);
      }

      // blur 当前活动元素
      if (document.activeElement && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    };

    // 立即执行 + 延迟执行
    performClose();
    setTimeout(performClose, 50);
    setTimeout(performClose, 150);
  }

  // 销毁
  destroy() {
    this.clearCloseTimer();
    if (this.hiddenButton?.parentNode) {
      this.hiddenButton.parentNode.removeChild(this.hiddenButton);
    }
    this.hiddenButton = null;
    this.inputElement = null;
    this.closeCallback = null;
    KeyboardController.instance = null;
  }

  // 清除关闭定时器
  private clearCloseTimer() {
    if (this.closeTimer !== null) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  // 检查键盘是否真的打开
  private isKeyboardOpen(): boolean {
    if (!this.inputElement) return false;
    // 检查输入框是否获焦 + 最近是否有打开操作
    const isFocused = document.activeElement === this.inputElement;
    const recentlyOpened = Date.now() - this.lastOpenTime < 1000;
    return isFocused || recentlyOpened;
  }
}

// 导出单例获取方法
export const getKeyboardController = () => KeyboardController.getInstance();

// 导出便捷方法
export const forceCloseKeyboard = () => {
  KeyboardController.getInstance().forceCloseSystemKeyboard();
};
